import { createFileRoute, Navigate, notFound } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { AgentSessionPurpose } from "@sx/protocol/autonomy/agents";

import { ConversationArtifactAddressContext } from "../../components/chat/artifact-registration";
import { useProject, useProjectId } from "../../shell/project";
import { AgentChatWorkspace } from "../../workspaces/agents/AgentChatWorkspace";
import { projectManagerOf, teamRosterQuery } from "../../workspaces/agents/api";
import { agentSessionQuery } from "../../workspaces/agents/queries";
import { agentPageSearch } from "../../workspaces/agents/agent-location";
import { agentWorkspaceEnabled } from "../../workspaces/agents/availability";
import { usePageArtifactAddress } from "../../workspaces/agents/beside/address";
import { useAgentSurfaces } from "../../workspaces/agents/beside/surfaces";
import { roleIcon } from "../../workspaces/agents/role-icon";

/** Before the project resolves, its address is not known; nothing is drawn from this. */
const NO_ADDRESS = { org: "", project: "" };

export const Route = createFileRoute(
  "/_console/p/$org/$project/loop/agent/$agentId/{-$sessionId}",
)({
  beforeLoad: () => {
    if (!agentWorkspaceEnabled) throw notFound();
  },
  // Started, not awaited: the page's frame draws from the address while these land. Once
  // per address: staying on it (a change of tab or artifact) asks for nothing again.
  loader: ({ context, params, cause }) => {
    if (cause === "stay") return;
    void import("../../workspaces/agents/prefetch").then(({ prefetchAgentPage }) =>
      prefetchAgentPage(
        context.queryClient,
        { org: params.org, project: params.project },
        params.agentId,
        params.sessionId,
      ),
    );
  },
  validateSearch: agentPageSearch,
  component: AgentChatRoute,
});

function AgentChatRoute() {
  const { agentId, sessionId } = Route.useParams();
  const { tab, revision, artifact, pane, pinned, compose } = Route.useSearch();
  const projectId = useProjectId();
  const address = useProject().address;
  const navigate = Route.useNavigate();
  // Its week opens beside its conversation when asked (its failing line's Why, an answer's card).
  const artifactAddress = usePageArtifactAddress({
    artifact,
    pane,
    pinned,
    onAddress: (next, replace) => void navigate({ search: (previous) => ({ ...previous, ...next }), replace }),
  });
  // The Project Manager's own conversation is the project's home: one place to talk to the
  // team, so its address here leads there. Its pieces of work, files and settings stay here.
  const roster = useQuery(teamRosterQuery(projectId));
  const leadAgent = projectManagerOf(roster.data);
  const lead = leadAgent?.agentId === agentId;
  const listed = roster.data?.agents.find((each) => each.agentId === agentId);
  const surfaces = useAgentSurfaces(
    projectId,
    address ?? NO_ADDRESS,
    agentId,
    listed?.name ?? "",
    listed ? roleIcon(listed) : "agent",
  );
  const opened = useQuery({
    ...agentSessionQuery(projectId, sessionId ?? ""),
    enabled: lead && sessionId !== undefined,
  });
  const home =
    lead &&
    tab === undefined &&
    artifact === undefined &&
    (sessionId === undefined || opened.data?.session.purpose === AgentSessionPurpose.INBOX);
  if (!address) return null;
  if (home) return <Navigate to="/p/$org/$project" params={address} replace />;
  // Not yet known whether the Project Manager's chat named here is its own conversation:
  // the page waits rather than drawing one it may leave at once.
  if (lead && tab === undefined && artifact === undefined && sessionId !== undefined && opened.isPending)
    return null;
  return (
    <ConversationArtifactAddressContext.Provider value={artifactAddress}>
    <AgentChatWorkspace
      projectId={projectId}
      address={address}
      agentId={agentId}
      sessionId={sessionId}
      onSelectSession={(nextSessionId, options) =>
        void navigate({
          to: "/p/$org/$project/loop/agent/$agentId/{-$sessionId}",
          params: { ...address, agentId, sessionId: nextSessionId },
          // Landing on the conversation (a replace) keeps the tab asked for; opening a
          // piece of work shows it.
          // An open artifact belongs to the conversation it came from.
          search: (previous) =>
            options?.replace
              ? previous
              : { ...previous, tab: undefined, artifact: undefined, pane: undefined, compose: undefined },
          replace: options?.replace,
        })
      }
      tab={tab}
      revision={revision}
      compose={compose}
      onTabChange={(next) =>
        void navigate({
          search: (previous) => ({ ...previous, tab: next, revision: undefined }),
        })
      }
      surfaces={lead ? undefined : surfaces}
    />
    </ConversationArtifactAddressContext.Provider>
  );
}
